import { useState } from 'react';
import { ouvreDocument } from '../../api/documents';
import { Btn } from '../../components/ui/Btn.jsx';
import { FRAIS_CAT } from '../../constants/accounting';
import { DEVISE, fmt } from '../../lib/currency';
import { dateSuisse } from '../../lib/dates';
import { localeDates, tr, trf } from '../../lib/i18n';
import { libelleMoyen } from '../../lib/paye-le';
import { tvaRecuperable } from '../../lib/vat';
import { C } from '../../theme/palette';
import { anneeLaPlusRecente, ChoixDesMois, moisDeLAnnee } from './ChoixDesMois.jsx';

/** « 10.10.2026 » ou « 2026-10-10 » → « 2026-10-10 », pour trier et filtrer. */
export function dateIso(date) {
  const s = String(date || '');
  const suisse = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(s);
  if (suisse)
    return suisse[3] + '-' + suisse[2].padStart(2, '0') + '-' + suisse[1].padStart(2, '0');
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : '';
}

/** « 2026-10 » écrit « octobre 2026 », dans la langue de l'écran. */
export function moisLisible(mois) {
  const m = /^(\d{4})-(\d{2})$/.exec(mois || '');
  if (!m) return mois || '';
  return new Date(+m[1], +m[2] - 1, 1).toLocaleDateString(localeDates(), {
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Une date (« 2026-10-10 ») est-elle dans la période choisie ?
 *
 * <p>« Comment choisir de quelle date à quelle date j'imprime, par semestre ou
 * par mois, pour éviter de tout imprimer ? » Les périodes : `tout`, une année
 * (`2026`), un mois (`2026-10`), un trimestre (`2026-T3`), un semestre
 * (`2026-S1`), ou `libre`, du … au … (dates comprises).
 *
 * @param {string} iso date de la pièce, AAAA-MM-JJ
 * @param {string|string[]} periode la période, ou les mois choisis (« AAAA-MM »)
 * @param {string} [du] début de la période libre, AAAA-MM-JJ
 * @param {string} [au] fin de la période libre, AAAA-MM-JJ
 * @returns {boolean} vrai si la pièce en fait partie
 */
export function dansLaPeriode(iso, periode, du, au) {
  // Des mois touchés un à un (« 2026-07 », « 2026-08 »…) : les cases des mois.
  if (Array.isArray(periode)) return !!iso && periode.includes(iso.slice(0, 7));
  if (!periode || periode === 'tout') return true;
  if (!iso) return false;
  if (periode === 'libre') return (!du || iso >= du) && (!au || iso <= au);
  const tri = /^(\d{4})-T([1-4])$/.exec(periode);
  const sem = /^(\d{4})-S([12])$/.exec(periode);
  if (tri || sem) {
    const [annee, n] = tri ? [tri[1], +tri[2]] : [sem[1], +sem[2]];
    const longueur = tri ? 3 : 6;
    const mois = +iso.slice(5, 7);
    return iso.startsWith(annee) && mois > (n - 1) * longueur && mois <= n * longueur;
  }
  return iso.startsWith(periode);
}

/**
 * Des mois choisis écrits en clair : « juillet 2026 – octobre 2026 » pour des
 * mois qui se suivent, « mars 2026, juillet 2026 » sinon, l'année seule
 * quand elle est entière.
 *
 * @param {string[]} mois mois choisis, « AAAA-MM »
 * @returns {string} le libellé
 */
export function libelleDesMois(mois) {
  const tries = [...new Set(mois || [])].filter((m) => /^\d{4}-\d{2}$/.test(m)).sort();
  if (!tries.length) return '';
  const rang = (m) => +m.slice(0, 4) * 12 + +m.slice(5, 7) - 1;
  const suites = [];
  tries.forEach((m) => {
    const derniere = suites[suites.length - 1];
    if (derniere && rang(m) === rang(derniere[derniere.length - 1]) + 1) derniere.push(m);
    else suites.push([m]);
  });
  return suites
    .map((suite) => {
      const debut = suite[0];
      const fin = suite[suite.length - 1];
      if (suite.length === 12 && debut.endsWith('-01')) return debut.slice(0, 4);
      return suite.length === 1
        ? moisLisible(debut)
        : moisLisible(debut) + ' – ' + moisLisible(fin);
    })
    .join(', ');
}

/** La période écrite en clair, en tête des feuilles. */
export function libellePeriode(periode, du, au) {
  if (Array.isArray(periode)) return libelleDesMois(periode);
  if (!periode || periode === 'tout') return '';
  if (periode === 'libre') {
    if (du && au) return trf('du {0} au {1}', dateSuisse(du), dateSuisse(au));
    if (du) return trf('dès le {0}', dateSuisse(du));
    if (au) return trf('jusqu’au {0}', dateSuisse(au));
    return '';
  }
  const tri = /^(\d{4})-T([1-4])$/.exec(periode);
  if (tri) return trf('Trimestre {0} · {1}', tri[2], tri[1]);
  const sem = /^(\d{4})-S([12])$/.exec(periode);
  if (sem) return trf('Semestre {0} · {1}', sem[2], sem[1]);
  return periode.length === 4 ? periode : moisLisible(periode);
}

/**
 * Le choix « Café + Repas » : les petits tickets du quotidien (le café du
 * matin, le resto de midi) ensemble sur les mêmes feuilles, par date.
 */
export const CAFE_ET_REPAS = 'cafe+repas';
const TYPES_CAFE_ET_REPAS = ['cafe', 'repas'];

/** « ☕ Café + 🍽 Repas / repr. », avec les mots déjà traduits. */
function libelleCafeEtRepas(icones) {
  return TYPES_CAFE_ET_REPAS.map(
    (k) => (icones ? FRAIS_CAT[k].icon + ' ' : '') + tr(FRAIS_CAT[k].lib),
  ).join(' + ');
}

/**
 * Les groupes à poser sur des feuilles A4 : une catégorie par groupe, dans
 * l'ordre du plan des catégories, chaque groupe trié par date, avec son total
 * et sa TVA récupérable — calculés comme sur la page des dépenses.
 *
 * @param {object[]} frais dépenses
 * @param {{categorie: string, periode: string}} choix catégorie (`toutes`,
 *   une clé de FRAIS_CAT ou CAFE_ET_REPAS, un seul groupe pour les deux) et
 *   période (`tout`, `AAAA` ou `AAAA-MM`)
 * @param {object} [soc] entreprise, pour les taux de TVA
 * @returns {{titre: string, ids: number[], total: string, tva: string}[]} les groupes
 */
export function groupesDesTickets(frais, { categorie, periode, du, au }, soc) {
  const retenues = (frais || []).filter((x) => {
    if (typeof x.id !== 'number') return false;
    if (categorie === CAFE_ET_REPAS) {
      if (!TYPES_CAFE_ET_REPAS.includes(x.type)) return false;
    } else if (categorie !== 'toutes' && x.type !== categorie) return false;
    return dansLaPeriode(dateIso(x.date), periode, du, au);
  });
  if (categorie === CAFE_ET_REPAS) {
    if (!retenues.length) return [];
    const lignes = [...retenues].sort(
      (a, b) => dateIso(a.date).localeCompare(dateIso(b.date)) || a.id - b.id,
    );
    const total = lignes.reduce((s, x) => s + (+x.montant || 0), 0);
    return [
      {
        titre: libelleCafeEtRepas(false),
        ids: lignes.map((x) => x.id),
        total: DEVISE + ' ' + fmt(total),
        tva: DEVISE + ' ' + fmt(tvaRecuperable(lignes, FRAIS_CAT, soc)),
        tickets: true,
      },
    ];
  }
  const ordre = Object.keys(FRAIS_CAT);
  const types = [...new Set(retenues.map((x) => x.type))].sort(
    (a, b) => (ordre.indexOf(a) + 1 || 999) - (ordre.indexOf(b) + 1 || 999),
  );
  return types.map((type) => {
    const lignes = retenues
      .filter((x) => x.type === type)
      .sort((a, b) => dateIso(a.date).localeCompare(dateIso(b.date)) || a.id - b.id);
    const total = lignes.reduce((s, x) => s + (+x.montant || 0), 0);
    return {
      titre: tr((FRAIS_CAT[type] || {}).lib || type),
      ids: lignes.map((x) => x.id),
      total: DEVISE + ' ' + fmt(total),
      tva: DEVISE + ' ' + fmt(tvaRecuperable(lignes, FRAIS_CAT, soc)),
      // Café, restaurant : des tickets de caisse, plusieurs par feuille, même
      // photographiés au téléphone (forme d'une page).
      tickets: TYPES_CAFE_ET_REPAS.includes(type),
    };
  });
}

/**
 * La demande des feuilles A4 de tickets : les mêmes feuilles depuis Dépenses
 * et depuis le classeur de la Compta.
 *
 * @param {object} p
 * @param {object[]} p.frais dépenses
 * @param {string} p.categorie `toutes`, une clé de FRAIS_CAT ou CAFE_ET_REPAS
 * @param {string} p.periode `tout`, `AAAA`, `AAAA-MM`, `AAAA-T1`…`T4`, `AAAA-S1`/`S2` ou `libre`
 * @param {string} [p.du] début de la période libre, AAAA-MM-JJ
 * @param {string} [p.au] fin de la période libre, AAAA-MM-JJ
 * @param {object} [p.soc] entreprise
 * @returns {object} le corps de la demande, pour `/depenses/tickets-a4`
 */
export function demandeTicketsA4({ frais, categorie, periode, du, au, soc }) {
  return {
    societe: soc?.nom || '',
    periode: libellePeriode(periode, du, au),
    devise: DEVISE,
    libelles: {
      pieces: tr('pièces'),
      page: tr('Page'),
      total: tr('Total'),
      tva: tr('dont TVA récupérable'),
      sansJustificatif: tr('Sans justificatif'),
      especes: libelleMoyen('especes'),
      carte: libelleMoyen('carte'),
      banque: libelleMoyen('banque'),
    },
    groupes: groupesDesTickets(frais, { categorie, periode, du, au }, soc),
  };
}

/**
 * « 🖨 Tickets sur A4 » : les justificatifs des dépenses, plusieurs par
 * feuille, une catégorie à la fois. Le patron classait ses tickets de café un
 * par feuille.
 *
 * @param {object} props propriétés du composant
 * @param {object[]} props.frais dépenses
 * @param {object} [props.soc] entreprise
 * @param {Function} props.onClose ferme le panneau
 * @param {string} [props.categorieInitiale] catégorie choisie à l'ouverture
 * @returns {import('react').ReactElement} le panneau de choix
 */
export function TicketsA4({ frais, soc, onClose, categorieInitiale = 'toutes' }) {
  const enregistrees = (frais || []).filter((x) => typeof x.id === 'number');
  const [categorie, setCategorie] = useState(categorieInitiale);
  // Les mois touchés, ceux de l'année la plus récente au départ.
  const [periode, setPeriode] = useState(() =>
    moisDeLAnnee(enregistrees, anneeLaPlusRecente(enregistrees)),
  );
  const du = '';
  const au = '';
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);

  const categories = Object.keys(FRAIS_CAT).filter((k) => enregistrees.some((x) => x.type === k));
  const groupes = groupesDesTickets(frais, { categorie, periode, du, au }, soc);
  const nombre = groupes.reduce((s, g) => s + g.ids.length, 0);

  const imprime = async () => {
    setErreur('');
    setEnCours(true);
    try {
      await ouvreDocument(
        '/depenses/tickets-a4',
        undefined,
        demandeTicketsA4({ frais, categorie, periode, du, au, soc }),
      );
    } catch (e) {
      if (!e?.dejaAffiche) setErreur(e?.message || tr('Les feuilles n’ont pas pu être créées.'));
    } finally {
      setEnCours(false);
    }
  };

  const champ = {
    width: '100%',
    border: '1px solid ' + C.border,
    borderRadius: 9,
    padding: '11px',
    fontSize: 13,
    marginBottom: 10,
    boxSizing: 'border-box',
    background: '#fff',
  };
  const etiquette = {
    fontSize: 9,
    fontWeight: 700,
    color: C.muted,
    margin: '2px 0 6px',
    textTransform: 'uppercase',
  };

  return (
    <div
      style={{
        background: '#fff',
        border: '2px solid ' + C.navy,
        borderRadius: 12,
        padding: 13,
        marginBottom: 12,
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 800, color: C.navy, marginBottom: 4 }}>
        🖨 {tr('Tickets sur A4')}
      </div>
      <div style={{ fontSize: 10.5, color: C.grey, marginBottom: 12, lineHeight: 1.5 }}>
        {tr(
          'Les tickets se rangent à plusieurs par feuille, selon leur taille. Chaque catégorie commence sur une nouvelle feuille, avec son total et la TVA récupérable.',
        )}
      </div>
      <div style={etiquette}>{tr('Catégorie')}</div>
      <select
        aria-label={tr('Catégorie')}
        value={categorie}
        onChange={(e) => setCategorie(e.target.value)}
        style={champ}
      >
        <option value="toutes">{tr('Toutes les catégories')}</option>
        {TYPES_CAFE_ET_REPAS.every((k) => categories.includes(k)) && (
          <option value={CAFE_ET_REPAS}>{libelleCafeEtRepas(true)}</option>
        )}
        {categories.map((k) => (
          <option key={k} value={k}>
            {FRAIS_CAT[k].icon} {tr(FRAIS_CAT[k].lib)}
          </option>
        ))}
      </select>
      <ChoixDesMois elements={enregistrees} mois={periode} onChange={setPeriode} />
      <div style={{ fontSize: 11, color: C.ink, marginBottom: 10 }}>
        {nombre} {tr('pièces')}
      </div>
      {erreur && (
        <div role="alert" style={{ fontSize: 10, color: C.red, marginBottom: 10 }}>
          {erreur}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <Btn
            label={enCours ? tr('Préparation du PDF…') : '🖨 ' + tr('Créer les feuilles A4')}
            color={C.navy}
            full
            disabled={!nombre || enCours}
            onClick={imprime}
          />
        </div>
        <Btn label={tr('Fermer')} ghost color={C.grey} onClick={onClose} />
      </div>
    </div>
  );
}
